import { useRef } from 'react';

export interface ReCaptchaFunctions {
  execute: (action: string) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string }
        ) => Promise<string>;
      };
    };
  }
}

export const useReCaptcha = (): ReCaptchaFunctions => {
  const waitPromiseRef = useRef<Promise<any> | null>(null);
  const timeoutMs = 5000;

  const execute = async (action: string): Promise<string> => {
    const grecaptcha = await waitForRecaptcha();

    return new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(async () => {
        try {
          const token = await grecaptcha.enterprise.execute(
            process.env.REACT_APP_RECAPTCHA_SITE_KEY as string,
            { action }
          );
          resolve(token);
        } catch (e) {
          reject(e);
        }
      });
    });
  };

  const waitForRecaptcha = (): Promise<any> => {
    if (waitPromiseRef.current) {
      return waitPromiseRef.current;
    }

    waitPromiseRef.current = new Promise((resolve, reject) => {
      const start = Date.now();

      const check = () => {
        const grecaptcha = window.grecaptcha;

        if (grecaptcha && grecaptcha.enterprise) {
          return resolve(grecaptcha);
        }

        if (Date.now() - start >= timeoutMs) {
          return reject(new Error('reCAPTCHA failed to load'));
        }

        setTimeout(check, 100);
      };

      check();
    });

    return waitPromiseRef.current;
  };

  return { execute };
};
